import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Check, X, Loader2, Printer, ShoppingCart, 
  AlertCircle, CreditCard, Phone, Smartphone, Building2
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { buildThermalReceipt } from '../utils/thermalReceiptTemplate';

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { cart, discount, discountType, subtotal, discountAmount, total, client, paymentType: initialPaymentType } = location.state || {};

  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountReceived, setAmountReceived] = useState('');
  const [processing, setProcessing] = useState(false);
  const [completedSale, setCompletedSale] = useState(null);
  const [paymentType, setPaymentType] = useState(initialPaymentType || 'full');
  const [mpesaConfirmationCode, setMpesaConfirmationCode] = useState('');

  // Validate client requirement on load (only for partial and credit)
  useEffect(() => {
    if ((paymentType === 'partial' || paymentType === 'credit') && !client) {
      toast.error('Client registration is required for partial/credit payments. Please go back and select a client.');
    }
  }, [paymentType, client]);

  const paymentMethods = [
    { id: 'cash', label: 'Cash', icon: CreditCard },
    { id: 'mpesa', label: 'M-Pesa', icon: Smartphone },
    { id: 'bank_transfer', label: 'Bank Transfer', icon: Building2 },
    { id: 'card', label: 'Card', icon: CreditCard },
  ];

  useEffect(() => {
    if (!cart) {
      navigate('/pos');
    }
  }, [cart, navigate]);

  if (!cart) return null;

  const change = Math.max(0, parseFloat(amountReceived || 0) - total);

  const handlePayment = async () => {
    if (paymentType !== 'credit' && (!amountReceived || parseFloat(amountReceived) < total)) {
      toast.error('Insufficient amount received');
      return;
    }

    // Validate M-Pesa confirmation code
    if (paymentMethod === 'mpesa' && paymentType !== 'credit' && !mpesaConfirmationCode.trim()) {
      toast.error('Please enter the M-Pesa confirmation code');
      return;
    }

    // Validate client requirement: only partial and credit require client
    if ((paymentType === 'partial' || paymentType === 'credit') && !client) {
      toast.error('Client registration is required for partial and credit payments');
      return;
    }

    setProcessing(true);
    try {
      const saleItems = cart.map(item => ({
        item_type: item.type,
        product_id: item.type === 'product' ? item.id : null,
        service_id: item.type === 'service' ? item.id : null,
        quantity: item.quantity,
        unit_price: item.price,
        subtotal: item.price * item.quantity,
        is_partial_sale: item.isPartial || false,
        units: item.units || null
      }));

      const payload = {
        items: saleItems,
        payment_method: paymentMethod,
        payment_type: paymentType,
        total_amount: total,
        discount: discountAmount,
        discount_type: discountType,
        amount_paid: paymentType === 'credit' ? 0 : parseFloat(amountReceived),
        balance_due: paymentType === 'credit' ? total : 0,
        client_id: client?.id || null,
        client_name: client ? `${client.first_name} ${client.last_name}` : null,
        notes: '',
        mpesa_confirmation_code: paymentMethod === 'mpesa' ? mpesaConfirmationCode.trim() : null
      };

      const response = await api.post('/pos/sales', payload);
      const saleData = response.data;
      setCompletedSale(saleData);
      toast.success('Sale completed successfully!');
      
      // Auto-print thermal receipt after successful payment
      setTimeout(() => {
        printThermalReceipt(saleData);
      }, 500);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process payment');
    }
    setProcessing(false);
  };

  const printThermalReceipt = (sale) => {
    // Map items to the format expected by thermalReceiptTemplate
    const mappedItems = (sale.items || []).map((it) => ({
      item_type: it.item_type,
      name: it.product_name || it.service_name || it.name,
      quantity: it.quantity,
      unit_price: it.unit_price,
      total_price: it.total_price
    }));

    const receiptData = {
      receipt_number: sale.receipt_number,
      sale_date: sale.sale_date,
      client_name:
        sale.client_name ||
        `${sale.client_first_name || ''} ${sale.client_last_name || ''}`.trim() ||
        'Walk-in Customer',
      cashier_name: sale.cashier_name,
      total_amount: sale.total_amount,
      discount: sale.discount,
      subtotal: sale.subtotal,
      tax: sale.tax,
      payment_method: sale.payment_method,
      amount_paid: sale.amount_paid,
      balance_due: sale.balance_due,
      payment_status: sale.payment_status,
      mpesa_confirmation_code: sale.mpesa_confirmation_code || null,
      items: mappedItems
    };

    const receiptHtml = buildThermalReceipt(receiptData);

    // Print via hidden iframe (thermal printer friendly 80mm format)
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);

    const cleanup = () => {
      try { document.body.removeChild(iframe); } catch (e) { /* ignore */ }
    };

    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(cleanup, 500);
      }
    };

    iframe.srcdoc = receiptHtml;
  };

  const handleNewSale = () => {
    navigate('/pos');
  };

  if (completedSale) {
    return (
      <div className="max-w-md mx-auto space-y-6 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-medium p-8 text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            paymentType !== 'full' ? 'bg-brand-orange-100' : 'bg-brand-green-100'
          }`}>
            <Check size={32} className={paymentType !== 'full' ? 'text-brand-orange' : 'text-brand-green'} />
          </div>
          <h2 className="text-xl font-bold text-neutral-dark mb-2">Payment Successful!</h2>
          <p className="text-lg font-bold text-neutral-gray">{completedSale?.receipt_number}</p>
          <p className="text-2xl font-bold text-brand-green mt-2">
            KES {parseFloat(completedSale?.total_amount).toLocaleString()}
          </p>
          
          {completedSale?.balance_due > 0 && (
            <div className="mt-3 p-3 bg-brand-orange-50 rounded-lg">
              <p className="text-sm text-brand-orange-700 font-medium">Balance Due</p>
              <p className="text-xl font-bold text-brand-orange">
                KES {parseFloat(completedSale.balance_due).toLocaleString()}
              </p>
            </div>
          )}

          <div className="space-y-3 mt-6">
            <button
              onClick={() => printThermalReceipt(completedSale)}
              className="w-full btn-info flex items-center justify-center space-x-2"
            >
              <Printer size={18} />
              <span>Print Receipt</span>
            </button>
            <button
              onClick={handleNewSale}
              className="w-full btn-primary"
            >
              New Sale
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto space-y-6 py-8">
      <div className="bg-white rounded-2xl shadow-sm border border-neutral-medium p-6">
        <h2 className="text-xl font-bold text-neutral-dark mb-4">Payment</h2>
        
        {/* Client Info Display */}
        <div className="mb-4 p-3 bg-neutral-light rounded-lg">
          <label className="block text-xs font-medium text-neutral-gray mb-1">Client</label>
          <p className="text-sm font-medium text-neutral-dark">
            {client ? `${client.first_name} ${client.last_name}` : 'Walk-in Client'}
          </p>
          {client && (
            <p className="text-xs text-neutral-gray">{client.phone || client.email}</p>
          )}
        </div>

        {/* Payment Type Selector */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Payment Type</label>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setPaymentType('full')}
              className={`p-3 rounded-xl border-2 transition-all ${
                paymentType === 'full'
                  ? 'border-brand-green bg-brand-green-50'
                  : 'border-neutral-medium hover:border-gray-300'
              }`}
            >
              <p className="text-sm font-medium text-neutral-dark">Full Payment</p>
              <p className="text-xs text-neutral-gray mt-0.5">Pay now</p>
            </button>
            <button
              onClick={() => setPaymentType('partial')}
              disabled={!client}
              className={`p-3 rounded-xl border-2 transition-all ${
                paymentType === 'partial'
                  ? 'border-brand-blue bg-brand-blue-50'
                  : client
                    ? 'border-neutral-medium hover:border-gray-300'
                    : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
              }`}
            >
              <p className="text-sm font-medium text-neutral-dark">Partial</p>
              <p className="text-xs text-neutral-gray mt-0.5">Pay part now</p>
            </button>
            <button
              onClick={() => setPaymentType('credit')}
              disabled={!client}
              className={`p-3 rounded-xl border-2 transition-all ${
                paymentType === 'credit'
                  ? 'border-brand-orange bg-brand-orange-50'
                  : client
                    ? 'border-neutral-medium hover:border-gray-300'
                    : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
              }`}
            >
              <p className="text-sm font-medium text-neutral-dark">Credit</p>
              <p className="text-xs text-neutral-gray mt-0.5">Pay later</p>
            </button>
          </div>
        </div>

        {/* Payment Type Indicator */}
        <div className={`p-4 rounded-xl mb-4 ${
          paymentType === 'full' ? 'bg-brand-green-50 border border-brand-green-200' :
          paymentType === 'partial' ? 'bg-brand-blue-50 border border-brand-blue-200' :
          'bg-brand-orange-50 border border-brand-orange-200'
        }`}>
          <div className="flex items-center space-x-2">
            {paymentType !== 'full' && <AlertCircle size={20} className={
              paymentType === 'partial' ? 'text-brand-blue' : 'text-brand-orange'
            } />}
            <span className="text-sm font-medium text-neutral-dark capitalize">{paymentType} Payment</span>
          </div>
          {paymentType !== 'full' && (
            <p className="text-xs text-neutral-gray mt-1">
              {paymentType === 'partial' ? 'Partial payment will be recorded' : 'Full amount to be paid later'}
            </p>
          )}
        </div>

        {/* Order Summary */}
        <div className="space-y-2 mb-6">
          {cart.map((item, index) => (
            <div key={index} className="flex justify-between text-sm">
              <span className="text-neutral-gray">{item.name} × {item.quantity}</span>
              <span className="text-neutral-dark">KES {(item.price * item.quantity).toLocaleString()}</span>
            </div>
          ))}
          {discountAmount > 0 && (
            <div className="flex justify-between text-sm text-brand-red">
              <span>Discount</span>
              <span>-KES {discountAmount.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-lg border-t border-neutral-medium pt-2 mt-2">
            <span className="text-neutral-dark">Total</span>
            <span className="text-brand-green">KES {total.toLocaleString()}</span>
          </div>
        </div>

        {/* Payment Method Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">Payment Method</label>
          <div className="grid grid-cols-2 gap-3">
            {paymentMethods.map(method => {
              const Icon = method.icon;
              return (
                <button
                  key={method.id}
                  onClick={() => setPaymentMethod(method.id)}
                  className={`flex items-center space-x-2 p-3 rounded-xl border transition-all ${
                    paymentMethod === method.id
                      ? 'border-brand-green bg-brand-green-50'
                      : 'border-neutral-medium hover:border-gray-300'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${
                    paymentMethod === method.id ? 'bg-brand-green-100' : 'bg-gray-100'
                  }`}>
                    <Icon size={20} className={
                      paymentMethod === method.id ? 'text-brand-green' : 'text-gray-600'
                    } />
                  </div>
                  <span className="text-sm font-medium text-neutral-dark">{method.label}</span>
                  {paymentMethod === method.id && <Check size={18} className="text-brand-green ml-auto" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Amount Input (for non-credit sales) */}
        {paymentType !== 'credit' && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Amount Received</label>
            <input
              type="number"
              value={amountReceived}
              onChange={(e) => setAmountReceived(e.target.value)}
              className="w-full px-4 py-3 border border-neutral-medium rounded-xl text-lg font-semibold focus:ring-2 focus:ring-brand-green focus:border-brand-green"
              placeholder="0.00"
              min={total}
              step="0.01"
            />
            {change > 0 && (
              <div className="mt-2 flex justify-between text-sm">
                <span className="text-neutral-gray font-semibold">Change</span>
                <span className="text-brand-green font-bold text-lg">KES {change.toLocaleString()}</span>
              </div>
            )}
          </div>
        )}

        {/* M-Pesa Confirmation Code (only when M-Pesa is selected) */}
        {paymentMethod === 'mpesa' && paymentType !== 'credit' && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              M-Pesa Confirmation Code <span className="text-brand-red">*</span>
            </label>
            <input
              type="text"
              value={mpesaConfirmationCode}
              onChange={(e) => setMpesaConfirmationCode(e.target.value.toUpperCase())}
              className="w-full px-4 py-3 border border-neutral-medium rounded-xl text-lg font-semibold focus:ring-2 focus:ring-brand-green focus:border-brand-green uppercase"
              placeholder="e.g. QL72K0HMB7"
              maxLength={30}
            />
            <p className="text-xs text-neutral-gray mt-1">
              Enter the M-Pesa confirmation code from the customer's payment message
            </p>
          </div>
        )}

        {/* Credit Sale Info */}
        {paymentType === 'credit' && (
          <div className="bg-brand-orange-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-brand-orange-700 font-medium">Credit Sale</p>
            <p className="text-2xl font-bold text-brand-orange mt-1">
              KES {total.toLocaleString()}
            </p>
            <p className="text-xs text-brand-orange mt-2">
              Full amount to be paid later
            </p>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={handlePayment}
          disabled={processing}
          className={`w-full py-3.5 rounded-xl font-medium text-white transition-colors disabled:opacity-50 shadow-sm ${
            paymentType === 'credit'
              ? 'bg-brand-orange hover:bg-brand-orange-700'
              : 'bg-brand-green hover:bg-brand-green-700'
          }`}
        >
          {processing ? (
            <span className="flex items-center justify-center space-x-2">
              <Loader2 size={18} className="animate-spin" />
              <span>Processing...</span>
            </span>
          ) : paymentType === 'credit' ? (
            'Complete Credit Sale'
          ) : (
            'Complete Payment'
          )}
        </button>
      </div>
    </div>
  );
};

export default Payment;